import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

// ---------------------------------------------------------------------------
// Design-Tokens
// ---------------------------------------------------------------------------
const DEFAULT_ACCENT_HEX = "#95c11f";
const PHOTO_MAX_DIMENSION = 1600; // längste Kante in px, verhindert riesige Smartphone-Fotos in der PDF
const PHOTO_JPEG_QUALITY = 0.72;
const INK = rgb(0.13, 0.13, 0.15);
const GRAY = rgb(0.45, 0.45, 0.47);
const GRAY_LIGHT = rgb(0.85, 0.85, 0.86);
const GRAY_BG = rgb(0.97, 0.97, 0.97);
const WHITE = rgb(1, 1, 1);

const ZAEHLER_EINHEITEN = {
   Strom: "kWh",
   Gas: "m³",
   "Wasser (kalt)": "m³",
   "Wasser (warm)": "m³",
   Fernwärme: "kWh",
   Heizung: "kWh",
};

const PAGE_WIDTH = 595.28; // A4
const PAGE_HEIGHT = 841.89;
const MARGIN = 42;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_HEIGHT = 40;
const CONTENT_BOTTOM = FOOTER_HEIGHT + 16;
const MAX_KEEP_TOGETHER_HEIGHT = 550; // ab dieser Blockhöhe lohnt sich "zusammenhalten" nicht mehr

const BETEILIGTE_PER_ROW = 3;
const BETEILIGTE_GAP = 12;
const BETEILIGTE_BASE_HEIGHT = 74;
const BETEILIGTE_NAME_SIZE = 10.5;
const BETEILIGTE_NAME_LINE_HEIGHT = 13;

const SIGNATURE_PER_ROW = 3;
const SIGNATURE_GAP = 14;
const SIGNATURE_TOP_HEIGHT = 104; // Bild + Trennlinie, unabhängig vom Namensumbruch fix
const SIGNATURE_NAME_SIZE = 8;
const SIGNATURE_NAME_LINE_HEIGHT = 10;
const SIGNATURE_BASE_HEIGHT = 130; // = TOP_HEIGHT + 11 (Label) + 1 Namenszeile*10 + 5 Rand

// ---------------------------------------------------------------------------
// Öffentliche API
// ---------------------------------------------------------------------------

/**
 * Erzeugt das Übergabeprotokoll als PDF (Uint8Array) aus den Formulardaten
 * der UebergabeFormular-Komponente.
 *
 * @param {object} data Formulardaten (Objekt, Beteiligte, Zähler, ...)
 * @param {object} [options]
 * @param {string} [options.accentColor] Akzentfarbe als Hex-Wert, z. B. "#95c11f".
 * @param {string} [options.logoUrl] URL zu einem PNG/JPG-Logo. pdf-lib kann kein SVG einbetten.
 * @param {number} [options.photoMaxDimension] Längste Kante, auf die Fotos vor dem Einbetten skaliert werden.
 * @param {number} [options.photoQuality] JPEG-Qualität (0–1) für die Neukompression der Fotos.
 */
export async function generateUebergabeprotokollPDF(
   { objekt, beteiligte, zaehler = [], schluessel = [], maengel = "", notizen = "", fotos = [], signatures = {} },
   {
      accentColor = DEFAULT_ACCENT_HEX,
      logoUrl = null,
      photoMaxDimension = PHOTO_MAX_DIMENSION,
      photoQuality = PHOTO_JPEG_QUALITY,
   } = {},
   estateForm
) {
   const pdfDoc = await PDFDocument.create();
   const propertyLabel = [objekt?.strasse, objekt?.ort].filter(Boolean).join(", ") || "Objekt";
   pdfDoc.setTitle(`Übergabeprotokoll – ${propertyLabel}`);
   pdfDoc.setProducer("Baur Immobilien – Übergabeprotokoll");

   const fonts = {
      font: await pdfDoc.embedFont(StandardFonts.Helvetica),
      bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
      oblique: await pdfDoc.embedFont(StandardFonts.HelveticaOblique),
   };

   const colors = buildAccentPalette(accentColor);
   const logoImage = logoUrl ? await embedImageFromUrl(pdfDoc, logoUrl) : null;

   const photoSizeOptions = { maxDimension: photoMaxDimension, quality: photoQuality };
   const zaehlerMitFoto = zaehler.filter((z) => z.foto);
   const embeddedZaehlerFotos = await embedAllImages(
      pdfDoc,
      zaehlerMitFoto.map((z) => ({ id: z.id, file: z.foto, caption: `Zählerstand ${z.art}` })),
      photoSizeOptions,
   );
   const embeddedFotos = await embedAllImages(
      pdfDoc,
      fotos.map((f, i) => ({ id: `foto-${i}`, file: f, caption: "Objektfoto" })),
      photoSizeOptions,
   );
   // Pro Person (Array-Index passend zu beteiligte.owner/buyer/makler) eine eigene Unterschrift.
   const embeddedSignatures = {
      owner: await embedSignatureList(pdfDoc, signatures.owner),
      buyer: await embedSignatureList(pdfDoc, signatures.buyer),
      makler: await embedSignatureList(pdfDoc, signatures.makler),
   };

   const layout = new PdfLayout(pdfDoc, fonts, { objekt, propertyLabel, colors, logoImage });

   layout.sectionObjektdaten(objekt || {},estateForm);
   layout.sectionBeteiligte(beteiligte || {});
   layout.sectionZaehler(zaehler);
   layout.sectionSchluessel(schluessel);
   layout.sectionZustand(maengel, notizen);
   layout.sectionFotos([...embeddedZaehlerFotos, ...embeddedFotos]);
   layout.sectionUnterschriften(beteiligte || {}, embeddedSignatures);

   layout.finalizeFootersAndPageNumbers();

   return pdfDoc.save();
}

export function pdfBytesToBlob(bytes) {
   return new Blob([bytes], { type: "application/pdf" });
}

/** Wandelt die PDF-Bytes in einen reinen Base64-String um (ohne "data:application/pdf;base64,"-Präfix). */
export function pdfBytesToBase64(bytes) {
   let binary = "";
   const chunkSize = 0x8000; // Vermeidet Stack-Overflow bei String.fromCharCode auf großen Arrays
   for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
   }
   return btoa(binary);
}

export function openPdfInNewTab(bytes) {
   const url = URL.createObjectURL(pdfBytesToBlob(bytes));
   window.open(url, "_blank");
   return url;
}

export function downloadPdf(bytes, filename = "uebergabeprotokoll.pdf") {
   const url = URL.createObjectURL(pdfBytesToBlob(bytes));
   const link = document.createElement("a");
   link.href = url;
   link.download = filename;
   document.body.appendChild(link);
   link.click();
   link.remove();
   setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ---------------------------------------------------------------------------
// Layout-Engine: verwaltet Seiten, Kopf-/Fußzeile und Umbrüche
// ---------------------------------------------------------------------------
class PdfLayout {
   constructor(pdfDoc, fonts, meta) {
      this.pdfDoc = pdfDoc;
      this.font = fonts.font;
      this.bold = fonts.bold;
      this.oblique = fonts.oblique;
      this.meta = meta;
      this.colors = meta.colors;
      this.logoImage = meta.logoImage;
      this.page = null;
      this.y = 0;
      this.addPage(true);
   }

   addPage(isFirst) {
      this.page = this.pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      this.y = this.drawHeader(isFirst);
   }

   /** Fügt bei Platzmangel eine neue Seite ein. Rückgabe zeigt an, ob umgebrochen wurde. */
   ensureSpace(height) {
      if (this.y - height < CONTENT_BOTTOM) {
         this.addPage(false);
         return true;
      }
      return false;
   }

   /** Erzwingt einen Seitenumbruch, unabhängig vom verbleibenden Platz. */
   pageBreak() {
      this.addPage(false);
   }

   drawHeader(isFirst) {
      const page = this.page;
      const top = PAGE_HEIGHT;
      page.drawRectangle({ x: 0, y: top - 8, width: PAGE_WIDTH, height: 8, color: this.colors.accent });

      if (isFirst) {
         const logoY = top - 8 - 26 - 34;
         this.drawLogo(MARGIN, logoY, 160, 50);

         const titleSize = 20;
         const title = "Übergabeprotokoll";
         const titleWidth = this.bold.widthOfTextAtSize(title, titleSize);
         page.drawText(title, {
            x: PAGE_WIDTH - MARGIN - titleWidth,
            y: logoY + 12,
            size: titleSize,
            font: this.bold,
            color: INK,
         });

         const dividerY = logoY - 14;
         page.drawLine({
            start: { x: MARGIN, y: dividerY },
            end: { x: PAGE_WIDTH - MARGIN, y: dividerY },
            thickness: 1,
            color: this.colors.accent,
         });

         const addressLine =
            [this.meta.objekt?.strasse, [this.meta.objekt?.plz, this.meta.objekt?.ort].filter(Boolean).join(" ")]
               .filter(Boolean)
               .join(", ") || "";
         page.drawText(addressLine, { x: MARGIN, y: dividerY - 16, size: 10.5, font: this.font, color: GRAY });

         const dateLine = formatDateTime(this.meta.objekt?.uebergabeDatum);
         if (dateLine) {
            const dateLabel = `Übergabe: ${dateLine}`;
            const w = this.font.widthOfTextAtSize(dateLabel, 10.5);
            page.drawText(dateLabel, {
               x: PAGE_WIDTH - MARGIN - w,
               y: dividerY - 16,
               size: 10.5,
               font: this.font,
               color: GRAY,
            });
         }

         return dividerY - 16 - 22;
      }

      page.drawText("Übergabeprotokoll", { x: MARGIN, y: top - 30, size: 11, font: this.bold, color: INK });
      const addressLine = this.meta.propertyLabel;
      const w = this.font.widthOfTextAtSize(addressLine, 9.5);
      page.drawText(addressLine, { x: PAGE_WIDTH - MARGIN - w, y: top - 29, size: 9.5, font: this.font, color: GRAY });
      const dividerY = top - 38;
      page.drawLine({
         start: { x: MARGIN, y: dividerY },
         end: { x: PAGE_WIDTH - MARGIN, y: dividerY },
         thickness: 0.75,
         color: GRAY_LIGHT,
      });
      return dividerY - 20;
   }

   // Zeigt das per logoUrl geladene Logo an; ohne URL (oder bei Ladefehler) einen Platzhalter.
   drawLogo(x, y, w, h) {
      const page = this.page;
      if (this.logoImage) {
         const dims = fitImage(this.logoImage, w, h);
         page.drawImage(this.logoImage, {
            x: x + (w - dims.width) / 2,
            y: y + (h - dims.height) / 2,
            width: dims.width,
            height: dims.height,
         });
         return;
      }
      page.drawRectangle({ x, y, width: w, height: h, color: this.colors.accentLight, borderColor: this.colors.accent, borderWidth: 1 });
      const label = "LOGO";
      const labelWidth = this.bold.widthOfTextAtSize(label, 11);
      page.drawText(label, {
         x: x + (w - labelWidth) / 2,
         y: y + h / 2 - 4,
         size: 11,
         font: this.bold,
         color: this.colors.accentDark,
      });
   }

   finalizeFootersAndPageNumbers() {
      const pages = this.pdfDoc.getPages();
      const total = pages.length;
      const generated = formatDateTime(new Date());
      pages.forEach((page, idx) => {
         page.drawLine({
            start: { x: MARGIN, y: FOOTER_HEIGHT },
            end: { x: PAGE_WIDTH - MARGIN, y: FOOTER_HEIGHT },
            thickness: 0.75,
            color: GRAY_LIGHT,
         });
         page.drawText(`Übergabeprotokoll · ${this.meta.propertyLabel}`, {
            x: MARGIN,
            y: FOOTER_HEIGHT - 14,
            size: 7.5,
            font: this.font,
            color: GRAY,
         });
         const right = `Seite ${idx + 1} von ${total} · erzeugt am ${generated}`;
         const w = this.font.widthOfTextAtSize(right, 7.5);
         page.drawText(right, { x: PAGE_WIDTH - MARGIN - w, y: FOOTER_HEIGHT - 14, size: 7.5, font: this.font, color: GRAY });
      });
   }

   // -------------------------------------------------------------------------
   // Bausteine
   // -------------------------------------------------------------------------
   // minFollowingHeight reserviert zusätzlich Platz für den Anfang des Inhalts, der auf die
   // Überschrift folgt - sonst könnte die Überschrift allein am Seitenende landen und der
   // Inhalt erst auf der nächsten Seite beginnen.
   sectionHeader(number, title, subtitle, minFollowingHeight = 0) {
      this.ensureSpace(36 + minFollowingHeight);
      const badgeSize = 22;
      const badgeX = MARGIN;
      const badgeY = this.y - badgeSize;
      this.page.drawRectangle({
         x: badgeX,
         y: badgeY,
         width: badgeSize,
         height: badgeSize,
         color: this.colors.accentLight,
         borderColor: this.colors.accent,
         borderWidth: 1,
      });
      const numText = String(number).padStart(2, "0");
      const numWidth = this.bold.widthOfTextAtSize(numText, 10);
      this.page.drawText(numText, {
         x: badgeX + (badgeSize - numWidth) / 2,
         y: badgeY + 7,
         size: 10,
         font: this.bold,
         color: this.colors.accentDark,
      });
      this.page.drawText(title, { x: badgeX + badgeSize + 12, y: badgeY + 6, size: 13, font: this.bold, color: INK });
      if (subtitle) {
         const titleWidth = this.bold.widthOfTextAtSize(title, 13);
         this.page.drawText(subtitle, {
            x: badgeX + badgeSize + 12 + titleWidth + 10,
            y: badgeY + 7.5,
            size: 8.5,
            font: this.font,
            color: GRAY,
         });
      }
      this.y = badgeY - 14;
   }

   /** Weicher Schatten hinter einer Karte, für einen etwas moderneren, flachen Tiefeneffekt. */
   drawCardShadow(x, yTop, width, height) {
      this.page.drawRectangle({ x: x + 2, y: yTop - height - 2, width, height, color: rgb(0, 0, 0), opacity: 0.06 });
   }

   /** Karte mit weißem Grund und dezentem, neutralem Rahmen (Akzentfarbe bewusst sparsam eingesetzt). */
   drawCardFrame(x, yTop, width, height) {
      this.drawCardShadow(x, yTop, width, height);
      this.page.drawRectangle({
         x,
         y: yTop - height,
         width,
         height,
         color: WHITE,
         borderColor: GRAY_LIGHT,
         borderWidth: 0.75,
      });
   }

   keyValueGrid(items) {
      const colWidth = CONTENT_WIDTH / 2;
      const rowHeight = 34;
      for (let i = 0; i < items.length; i += 2) {
         this.ensureSpace(rowHeight);
         items.slice(i, i + 2).forEach((item, idx) => {
            const x = MARGIN + idx * colWidth;
            this.page.drawText(item.label.toUpperCase(), { x, y: this.y - 10, size: 7.5, font: this.bold, color: GRAY });
            this.page.drawText(item.value ? String(item.value) : "–", {
               x,
               y: this.y - 24,
               size: 10.5,
               font: this.font,
               color: INK,
            });
         });
         this.y -= rowHeight;
      }
      this.y -= 6;
   }

   // Berechnet Namens-Zeilenumbruch + benötigte Kartenhöhe für eine Zeile von bis zu perRow Personen,
   // ohne zu zeichnen - genutzt sowohl zum Zeichnen als auch zur Vorausberechnung für den Orphan-Schutz.
   beteiligteRowLayout(rowEntries) {
      const cardWidth = (CONTENT_WIDTH - BETEILIGTE_GAP * (BETEILIGTE_PER_ROW - 1)) / BETEILIGTE_PER_ROW;
      const maxTextWidth = cardWidth - 28;
      const nameLines = rowEntries.map((entry) => wrapText(this.bold, entry.data?.name || "–", BETEILIGTE_NAME_SIZE, maxTextWidth));
      const maxLines = Math.max(1, ...nameLines.map((lines) => lines.length));
      const cardHeight = BETEILIGTE_BASE_HEIGHT + (maxLines - 1) * BETEILIGTE_NAME_LINE_HEIGHT;
      return { cardWidth, maxTextWidth, nameLines, cardHeight };
   }

   // Verkäufer/Käufer können jetzt mehrfach angelegt werden (Arrays) - eine Karte pro Person,
   // im 3er-Raster umbrechend statt fixer 3-Spalten-Aufteilung.
   beteiligteCards(beteiligte) {
      const entries = buildBeteiligteEntries(beteiligte);

      if (!entries.length) {
         this.ensureSpace(24);
         this.page.drawText("Keine Beteiligten erfasst.", { x: MARGIN, y: this.y - 14, size: 9.5, font: this.oblique, color: GRAY });
         this.y -= 30;
         return;
      }

      for (let i = 0; i < entries.length; i += BETEILIGTE_PER_ROW) {
         const rowEntries = entries.slice(i, i + BETEILIGTE_PER_ROW);
         // Namen umbrechen statt abzuschneiden; alle Karten der Zeile bekommen dieselbe Höhe,
         // damit sie bündig bleiben, auch wenn nur eine Person einen langen Namen hat.
         const { cardWidth, maxTextWidth, nameLines: rowNameLines, cardHeight } = this.beteiligteRowLayout(rowEntries);
         const gap = BETEILIGTE_GAP;
         const nameSize = BETEILIGTE_NAME_SIZE;
         const nameLineHeight = BETEILIGTE_NAME_LINE_HEIGHT;

         this.ensureSpace(cardHeight);
         const y = this.y;
         rowEntries.forEach((entry, idx) => {
            const x = MARGIN + idx * (cardWidth + gap);
            this.drawCardFrame(x, y, cardWidth, cardHeight);
            this.page.drawText(entry.label.toUpperCase(), { x: x + 14, y: y - 20, size: 7.5, font: this.bold, color: this.colors.accentDark });
            rowNameLines[idx].forEach((line, lineIdx) => {
               this.page.drawText(line, {
                  x: x + 14,
                  y: y - 38 - lineIdx * nameLineHeight,
                  size: nameSize,
                  font: this.bold,
                  color: INK,
               });
            });
            this.page.drawText(truncateToWidth(this.font, entry.data?.email || "–", 8.5, maxTextWidth), {
               x: x + 14,
               y: y - cardHeight + 20,
               size: 8.5,
               font: this.font,
               color: GRAY,
            });
         });
         this.y -= cardHeight + 16;
      }
   }

   // Minimalistische Zeilen-Tabelle: kein Zellrahmen, nur dezente Trennlinien.
   table(headers, rows, colWidths) {
      const rowHeight = 24;
      const headerHeight = 26;
      const totalWidth = colWidths.reduce((a, b) => a + b, 0);

      // Passt die ganze Tabelle auf eine Seite, wird sie als Ganzes verschoben statt zwischen
      // Kopf- und Datenzeilen umzubrechen - vermeidet z. B. "Kopfzeile + 1 Zeile" auf der einen
      // und den Rest auf der nächsten Seite. Ist die Tabelle zu groß dafür, bleibt der normale
      // zeilenweise Umbruch mit wiederholter Kopfzeile als Fallback erhalten.
      const totalTableHeight = headerHeight + Math.max(rows.length, 1) * rowHeight;
      if (totalTableHeight <= MAX_KEEP_TOGETHER_HEIGHT) {
         this.ensureSpace(totalTableHeight);
      }

      const drawHeaderRow = () => {
         this.ensureSpace(headerHeight);
         let x = MARGIN;
         headers.forEach((h, i) => {
            this.page.drawText(h.toUpperCase(), { x: x + 2, y: this.y - headerHeight + 10, size: 8, font: this.bold, color: this.colors.accentDark });
            x += colWidths[i];
         });
         this.page.drawLine({
            start: { x: MARGIN, y: this.y - headerHeight + 4 },
            end: { x: MARGIN + totalWidth, y: this.y - headerHeight + 4 },
            thickness: 1.5,
            color: this.colors.accent,
         });
         this.y -= headerHeight;
      };

      drawHeaderRow();

      if (rows.length === 0) {
         this.ensureSpace(rowHeight);
         this.page.drawText("Keine Einträge.", { x: MARGIN + 2, y: this.y - rowHeight + 8, size: 9, font: this.oblique, color: GRAY });
         this.y -= rowHeight;
      }

      rows.forEach((cells, rIdx) => {
         const broke = this.ensureSpace(rowHeight);
         if (broke) drawHeaderRow();
         if (rIdx % 2 === 1) {
            this.page.drawRectangle({ x: MARGIN, y: this.y - rowHeight, width: totalWidth, height: rowHeight, color: GRAY_BG });
         }
         let x = MARGIN;
         cells.forEach((cellText, i) => {
            this.page.drawText(cellText ? String(cellText) : "–", {
               x: x + 2,
               y: this.y - rowHeight + 8,
               size: 9.5,
               font: this.font,
               color: INK,
            });
            x += colWidths[i];
         });
         this.page.drawLine({
            start: { x: MARGIN, y: this.y - rowHeight },
            end: { x: MARGIN + totalWidth, y: this.y - rowHeight },
            thickness: 0.5,
            color: GRAY_LIGHT,
         });
         this.y -= rowHeight;
      });

      this.y -= 14;
   }

   // Jeder Zähler-Eintrag als eigene Karte. Zugehörige Fotos liegen gebündelt in der Fotodokumentation.
   zaehlerCards(zaehler) {
      if (!zaehler.length) {
         this.ensureSpace(24);
         this.page.drawText("Keine Zähler erfasst.", { x: MARGIN, y: this.y - 14, size: 9.5, font: this.oblique, color: GRAY });
         this.y -= 30;
         return;
      }

      const cardHeight = 54;
      const gap = 8;

      zaehler.forEach((z) => {
         this.ensureSpace(cardHeight);
         const y = this.y;
         const x = MARGIN;
         this.drawCardFrame(x, y, CONTENT_WIDTH, cardHeight);

         const textX = x + 16;
         this.page.drawText(z.art || "Zähler", { x: textX, y: y - 22, size: 11, font: this.bold, color: INK });
         const unit = ZAEHLER_EINHEITEN[z.art] || "";
         const standText = z.stand ? `Stand: ${z.stand}${unit ? ` ${unit}` : ""}` : null;
         const details = [z.nummer ? `Nr. ${z.nummer}` : null, standText].filter(Boolean).join("   ·   ") || "";
         this.page.drawText(details, { x: textX, y: y - 39, size: 9.5, font: this.font, color: GRAY });

         this.y -= cardHeight + gap;
      });
      this.y -= 4;
   }

   textBox(label, text) {
      const paddingX = 10;
      const { value, lines, boxHeight } = layoutTextBox(this.font, text, CONTENT_WIDTH - paddingX * 2);

      this.ensureSpace(boxHeight + 20);
      this.page.drawText(label.toUpperCase(), { x: MARGIN, y: this.y - 10, size: 8, font: this.bold, color: GRAY });
      this.y -= 16;
      this.page.drawRectangle({
         x: MARGIN,
         y: this.y - boxHeight,
         width: CONTENT_WIDTH,
         height: boxHeight,
         color: WHITE,
         borderColor: GRAY_LIGHT,
         borderWidth: 0.75,
      });
      let ly = this.y - 16;
      lines.forEach((line) => {
         this.page.drawText(line, {
            x: MARGIN + paddingX,
            y: ly,
            size: 9.5,
            font: value ? this.font : this.oblique,
            color: value ? INK : GRAY,
         });
         ly -= TEXTBOX_LINE_HEIGHT;
      });
      this.y -= boxHeight + 14;
   }

   photoGrid(photos) {
      if (!photos.length) return;
      const perRow = 2;
      const gap = 12;
      const cellWidth = (CONTENT_WIDTH - gap * (perRow - 1)) / perRow;
      const cellImgHeight = cellWidth * 0.68;
      const cellHeight = cellImgHeight + 26;

      for (let i = 0; i < photos.length; i += perRow) {
         this.ensureSpace(cellHeight);
         const y = this.y;
         photos.slice(i, i + perRow).forEach((p, idx) => {
            const x = MARGIN + idx * (cellWidth + gap);
            this.drawCardShadow(x, y, cellWidth, cellHeight);
            this.page.drawRectangle({
               x,
               y: y - cellHeight,
               width: cellWidth,
               height: cellHeight,
               borderColor: GRAY_LIGHT,
               borderWidth: 0.75,
               color: WHITE,
            });
            const dims = fitImage(p.image, cellWidth - 8, cellImgHeight - 6);
            this.page.drawImage(p.image, {
               x: x + (cellWidth - dims.width) / 2,
               y: y - 6 - dims.height,
               width: dims.width,
               height: dims.height,
            });
            const caption = truncate(p.caption || "", 26);
            const capWidth = this.font.widthOfTextAtSize(caption, 7.5);
            this.page.drawText(caption, { x: x + (cellWidth - capWidth) / 2, y: y - cellHeight + 6, size: 7.5, font: this.font, color: GRAY });
         });
         this.y -= cellHeight + gap;
      }
      this.y -= 4;
   }

   // Ein Unterschriftsfeld je Person (Array-Index passend zu beteiligte.owner/buyer/makler),
   // im 3er-Raster umbrechend - analog zu beteiligteCards.
   // Namens-Zeilenumbruch + benötigte Boxhöhe für eine Zeile von bis zu SIGNATURE_PER_ROW Personen,
   // ohne zu zeichnen - Bild + Trennlinie bleiben fix, nur der Fuß (Label + Name) wächst nach unten.
   signatureRowLayout(rowEntries) {
      const boxWidth = (CONTENT_WIDTH - SIGNATURE_GAP * (SIGNATURE_PER_ROW - 1)) / SIGNATURE_PER_ROW;
      const maxTextWidth = boxWidth - 28;
      const nameLines = rowEntries.map((entry) => wrapText(this.font, entry.data?.name || "–", SIGNATURE_NAME_SIZE, maxTextWidth));
      const maxLines = Math.max(1, ...nameLines.map((lines) => lines.length));
      const boxHeight = SIGNATURE_BASE_HEIGHT + (maxLines - 1) * SIGNATURE_NAME_LINE_HEIGHT;
      return { boxWidth, maxTextWidth, nameLines, boxHeight };
   }

   signatureBlock(beteiligte, embeddedSignatures) {
      const entries = buildSignatureEntries(beteiligte, embeddedSignatures);

      if (!entries.length) {
         this.ensureSpace(24);
         this.page.drawText("Keine Unterschriften erforderlich.", { x: MARGIN, y: this.y - 14, size: 9.5, font: this.oblique, color: GRAY });
         this.y -= 30;
         return;
      }

      for (let i = 0; i < entries.length; i += SIGNATURE_PER_ROW) {
         const rowEntries = entries.slice(i, i + SIGNATURE_PER_ROW);
         const { boxWidth, nameLines: rowNameLines, boxHeight } = this.signatureRowLayout(rowEntries);

         this.ensureSpace(boxHeight + 20);
         const y = this.y;
         rowEntries.forEach((entry, idx) => {
            const x = MARGIN + idx * (boxWidth + SIGNATURE_GAP);
            this.drawCardFrame(x, y, boxWidth, boxHeight);
            if (entry.sig) {
               const dims = fitImage(entry.sig, boxWidth - 20, SIGNATURE_TOP_HEIGHT - 16);
               this.page.drawImage(entry.sig, {
                  x: x + 14 + (boxWidth - 14 - dims.width) / 2,
                  y: y - 16 - dims.height,
                  width: dims.width,
                  height: dims.height,
               });
            }
            this.page.drawLine({
               start: { x: x + 16, y: y - SIGNATURE_TOP_HEIGHT },
               end: { x: x + boxWidth - 12, y: y - SIGNATURE_TOP_HEIGHT },
               thickness: 0.75,
               color: GRAY_LIGHT,
            });
            this.page.drawText(entry.label.toUpperCase(), {
               x: x + 16,
               y: y - SIGNATURE_TOP_HEIGHT - 11,
               size: 7.5,
               font: this.bold,
               color: this.colors.accentDark,
            });
            rowNameLines[idx].forEach((line, lineIdx) => {
               this.page.drawText(line, {
                  x: x + 16,
                  y: y - SIGNATURE_TOP_HEIGHT - 11 - (lineIdx + 1) * SIGNATURE_NAME_LINE_HEIGHT,
                  size: SIGNATURE_NAME_SIZE,
                  font: this.font,
                  color: GRAY,
               });
            });
         });
         this.y -= boxHeight + 20;
      }
   }

   // -------------------------------------------------------------------------
   // Abschnitte
   // -------------------------------------------------------------------------
   sectionObjektdaten(objekt,estateForm) {
      this.sectionHeader(1, "Objektdaten", "Adresse und Eckdaten der Übergabe", 34);
      this.keyValueGrid([
         ...estateForm.map(item=>({label: item.name, value: objekt[item.key]})),
         /* { label: "Adresse", value: objekt.strasse },
         { label: "PLZ / Ort", value: [objekt.plz, objekt.ort].filter(Boolean).join(" ") },
         { label: "Wohn-/Nutzfläche", value: objekt.wohnflaeche ? `${objekt.wohnflaeche} m²` : "" },
         { label: "Etage", value: objekt.etage }, */
         { label: "Datum der Übergabe", value: formatDateTime(objekt.uebergabeDatum) },
      ]);
   }

   sectionBeteiligte(beteiligte) {
      // Reserviert die tatsächliche Höhe der ersten Kartenzeile (inkl. eventuellem Namensumbruch),
      // damit die Überschrift nicht isoliert am Seitenende landet.
      const entries = buildBeteiligteEntries(beteiligte);
      const firstRowHeight = entries.length ? this.beteiligteRowLayout(entries.slice(0, BETEILIGTE_PER_ROW)).cardHeight : 24;
      this.sectionHeader(2, "Beteiligte", "Verkäufer, Käufer und Makler", firstRowHeight);
      this.beteiligteCards(beteiligte);
   }

   sectionZaehler(zaehler) {
      this.sectionHeader(3, "Zählerstände", "Ablesung bei Übergabe", zaehler.length ? 54 : 24);
      this.zaehlerCards(zaehler);
   }

   sectionSchluessel(schluessel) {
      const tableHeight = 26 + Math.max(schluessel.length, 1) * 24; // muss zu table() passen
      this.sectionHeader(4, "Schlüssel & Übergabegegenstände", "", Math.min(tableHeight, MAX_KEEP_TOGETHER_HEIGHT));
      const rows = schluessel.map((s) => [s.bezeichnung, String(s.anzahl ?? "")]);
      this.table(["Bezeichnung", "Anzahl"], rows, [CONTENT_WIDTH - 100, 100]);
   }

   sectionZustand(maengel, notizen) {
      // Höhe hängt vom tatsächlichen Text ab (Zeilenumbruch) - deshalb hier exakt vorausberechnet,
      // statt einen festen Schätzwert zu reservieren, der bei langen Texten zu klein wäre.
      const { boxHeight } = layoutTextBox(this.font, maengel, CONTENT_WIDTH - 20);
      this.sectionHeader(5, "Zustand, Mängel & Notizen", "", 16 + boxHeight);
      this.textBox("Festgestellte Mängel", maengel);
      this.textBox("Vereinbarungen / sonstige Notizen", notizen);
   }

   // Kein Abschnitt, wenn keine Fotos vorliegen (weder Zähler- noch allgemeine Fotos).
   sectionFotos(photos) {
      if (!photos.length) return;
      this.sectionHeader(6, "Fotodokumentation", `${photos.length} Foto(s)`, photoGridRowHeight());
      this.photoGrid(photos);
   }

   sectionUnterschriften(beteiligte, embeddedSignatures) {
      const entries = buildSignatureEntries(beteiligte, embeddedSignatures);
      const firstRowHeight = entries.length
         ? this.signatureRowLayout(entries.slice(0, SIGNATURE_PER_ROW)).boxHeight
         : 24;
      this.sectionHeader(7, "Unterschriften", "Bestätigung der Übergabe durch alle Beteiligten", firstRowHeight);
      this.signatureBlock(beteiligte, embeddedSignatures);
   }
}

// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------
async function embedAllImages(pdfDoc, items, sizeOptions = {}) {
   const { maxDimension = PHOTO_MAX_DIMENSION, quality = PHOTO_JPEG_QUALITY } = sizeOptions;
   const results = [];
   for (const item of items) {
      if (!item.file) continue;
      try {
         const jpegBytes = await downscaleToJpeg(item.file, maxDimension, quality);
         const image = await pdfDoc.embedJpg(jpegBytes);
         results.push({ id: item.id, image, caption: item.caption });
      } catch (err) {
         console.warn("Foto konnte nicht komprimiert werden, verwende Original:", item.caption, err);
         try {
            const bytes = new Uint8Array(await item.file.arrayBuffer());
            const isPng = (item.file.type || "").includes("png");
            const image = isPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
            results.push({ id: item.id, image, caption: item.caption });
         } catch (fallbackErr) {
            console.warn("Bild konnte nicht eingebettet werden:", item.caption, fallbackErr);
         }
      }
   }
   return results;
}

// Skaliert ein Foto auf eine maximale Kantenlänge herunter und komprimiert es als JPEG,
// damit Smartphone-Fotos (oft mehrere MB, mehrere tausend Pixel) die PDF nicht aufblähen.
async function downscaleToJpeg(file, maxDimension, quality) {
   const bitmap = await createImageBitmap(file);
   try {
      const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0, width, height);

      const blob = await new Promise((resolve, reject) =>
         canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob fehlgeschlagen"))), "image/jpeg", quality),
      );
      return new Uint8Array(await blob.arrayBuffer());
   } finally {
      bitmap.close?.();
   }
}

async function embedImageFromUrl(pdfDoc, url) {
   try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      const contentType = res.headers.get("content-type") || "";
      const isPng = contentType.includes("png") || url.toLowerCase().endsWith(".png");
      return isPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
   } catch (err) {
      console.warn("Logo konnte nicht geladen werden, verwende Platzhalter:", url, err);
      return null;
   }
}

// Wandelt einen Hex-Farbwert ("#95c11f" oder "95c11f") in 0..1-RGB-Komponenten um.
function hexToRgbComponents(hex) {
   const clean = String(hex).replace("#", "").trim();
   const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
   const value = parseInt(full, 16);
   if (full.length !== 6 || Number.isNaN(value)) return hexToRgbComponents(DEFAULT_ACCENT_HEX);
   return { r: ((value >> 16) & 255) / 255, g: ((value >> 8) & 255) / 255, b: (value & 255) / 255 };
}

function tintColor({ r, g, b }, amount) {
   return { r: r + (1 - r) * amount, g: g + (1 - g) * amount, b: b + (1 - b) * amount };
}

function shadeColor({ r, g, b }, amount) {
   return { r: r * (1 - amount), g: g * (1 - amount), b: b * (1 - amount) };
}

// Leitet aus einer einzigen Akzentfarbe die im Layout benötigten Varianten ab
// (heller Tint für Badges/Boxen, dunklere Version für Text auf hellem Grund).
function buildAccentPalette(accentHex) {
   const base = hexToRgbComponents(accentHex);
   const light = tintColor(base, 0.85);
   const dark = shadeColor(base, 0.49);
   return {
      accent: rgb(base.r, base.g, base.b),
      accentLight: rgb(light.r, light.g, light.b),
      accentDark: rgb(dark.r, dark.g, dark.b),
   };
}

async function embedDataUrlPng(pdfDoc, dataUrl) {
   try {
      const base64 = dataUrl.split(",")[1] ?? dataUrl;
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      return await pdfDoc.embedPng(bytes);
   } catch (err) {
      console.warn("Unterschrift konnte nicht eingebettet werden", err);
      return null;
   }
}

// Bettet eine Liste von Signatur-DataURLs ein (eine je Person, Index passend zu beteiligte.<rolle>).
async function embedSignatureList(pdfDoc, dataUrls) {
   const results = [];
   for (const dataUrl of dataUrls || []) {
      results.push(dataUrl ? await embedDataUrlPng(pdfDoc, dataUrl) : null);
   }
   return results;
}

const TEXTBOX_LINE_HEIGHT = 13;

// Berechnet Zeilenumbruch und Höhe einer Textbox vorab (ohne zu zeichnen), damit sectionZustand()
// die tatsächliche Höhe des ersten Textblocks für den Orphan-Schutz der Überschrift kennt.
function layoutTextBox(font, text, maxWidth) {
   const value = text?.trim();
   const lines = wrapText(font, value || "", 9.5, maxWidth);
   const boxHeight = Math.max(40, lines.length * TEXTBOX_LINE_HEIGHT + 20);
   return { value, lines, boxHeight };
}

// Muss mit den perRow/gap/Verhältnis-Werten in photoGrid() übereinstimmen.
function photoGridRowHeight() {
   const perRow = 2;
   const gap = 12;
   const cellWidth = (CONTENT_WIDTH - gap * (perRow - 1)) / perRow;
   return cellWidth * 0.68 + 26;
}

function fitImage(image, maxW, maxH) {
   const ratio = Math.min(maxW / image.width, maxH / image.height, 1);
   return { width: image.width * ratio, height: image.height * ratio };
}

function truncate(str, n) {
   return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}

// Kürzt anhand der tatsächlich gerenderten Breite (statt einer festen Zeichenanzahl), damit lange
// Namen/Bezeichnungen nie über den verfügbaren Platz hinauslaufen - unabhängig von Schriftbreite.
function truncateToWidth(font, text, size, maxWidth) {
   const value = String(text ?? "");
   if (!value || font.widthOfTextAtSize(value, size) <= maxWidth) return value;
   let result = value;
   while (result.length > 1 && font.widthOfTextAtSize(`${result}…`, size) > maxWidth) {
      result = result.slice(0, -1);
   }
   return `${result}…`;
}

function buildBeteiligteEntries(beteiligte) {
   return [
      ...(beteiligte.owner || []).map((data) => ({ label: "Verkäufer", data })),
      ...(beteiligte.buyer || []).map((data) => ({ label: "Käufer", data })),
      ...(beteiligte.makler || []).map((data) => ({ label: "Makler", data })),
   ];
}

function buildSignatureEntries(beteiligte, embeddedSignatures) {
   return [
      ...(beteiligte.owner || []).map((data, i) => ({ label: "Verkäufer", data, sig: embeddedSignatures.owner?.[i] })),
      ...(beteiligte.buyer || []).map((data, i) => ({ label: "Käufer", data, sig: embeddedSignatures.buyer?.[i] })),
      ...(beteiligte.makler || []).map((data, i) => ({ label: "Makler", data, sig: embeddedSignatures.makler?.[i] })),
   ];
}

function wrapText(font, text, size, maxWidth) {
   const paragraphs = String(text).split(/\r?\n/);
   const lines = [];
   paragraphs.forEach((para) => {
      const words = para.split(/\s+/).filter(Boolean);
      if (words.length === 0) {
         lines.push("");
         return;
      }
      let current = "";
      words.forEach((word) => {
         const candidate = current ? `${current} ${word}` : word;
         if (current && font.widthOfTextAtSize(candidate, size) > maxWidth) {
            lines.push(current);
            current = word;
         } else {
            current = candidate;
         }
      });
      if (current) lines.push(current);
   });
   return lines;
}

function formatDateTime(value) {
   if (!value) return "";
   const date = value instanceof Date ? value : new Date(value);
   if (Number.isNaN(date.getTime())) return "";
   return date.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
